import { t } from "i18next";
import React from "react";
import Highlighter from "react-highlight-words";
import i18next from "src/i18n/config";
import { useDispatch } from "src/store";
import { TextLink } from "src/ui/shared/TextLink";
import { makeStyles } from "tss-react/mui";

import { showDetails } from "@dashboard/devices/store";
import { Group } from "@dashboard/devices/types";
import { hasEffectivePolicy } from "@dashboard/devices/utils/license";
import { Tooltip } from "@shared/Tooltip";
import { useIsClipped } from "@shared/useIsClipped";
import { ColumnDef, ColumnFiltersState, ExpandedState, FilterFnOption, Row } from "@tanstack/react-table";

import { ActionCell } from "./components/table/ActionCell";
import DevicesCountCell from "./components/table/DevicesCountCell";
import { ExpandedCell, ExpandedHeader } from "./components/table/Expander";
import { PolicyCell } from "./components/table/PolicyCell";
import { ComplianceStatusIcon, CoverageStatusIcon, OnlineStatusIcon } from "./components/table/StatusCells";
import TableCell from "./components/table/TableCell";
import { getParentGroups, toWholePercent } from "./utils";

const useStyles = makeStyles()((theme) => ({
    highlightedText: {
        backgroundColor: theme.palette.emteria.warning,
    },
    description: {
        display: "block",
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
}));

const DescriptionCell = React.memo(({ value, globalFilter }: { value: string; globalFilter?: string }) => {
    const { classes } = useStyles();
    const { ref, clipped } = useIsClipped(value);

    return (
        <Tooltip title={value} hide={!clipped} disableChildrenWrapper>
            <span ref={ref} className={classes.description}>
                <Highlighter
                    searchWords={globalFilter ? [globalFilter] : []}
                    textToHighlight={value}
                    autoEscape
                    highlightClassName={classes.highlightedText}
                />
            </span>
        </Tooltip>
    );
});
DescriptionCell.displayName = "DescriptionCell";

const NameCell = ({ id, value, globalFilter }: { id: number; value: string; globalFilter?: string }) => {
    const { classes } = useStyles();
    const dispatch = useDispatch();

    const showGroup: React.MouseEventHandler<HTMLElement> = (event) => {
        // The name sits inside the expander of a group with subgroups, whose click toggles them open.
        event.stopPropagation();
        dispatch(showDetails({ type: "group", selectedId: id }));
    };

    return (
        <TextLink
            onClick={showGroup}
            tooltip={i18next.t("pages:groupPage.tooltip.showGroupDetails")}
            truncatedTooltip={value}
            sx={{ maxWidth: 280 }}
        >
            <Highlighter
                searchWords={globalFilter ? [globalFilter] : []}
                textToHighlight={value ?? ""}
                autoEscape
                highlightClassName={classes.highlightedText}
            />
        </TextLink>
    );
};

export interface GroupOverviewData {
    name: string;
    description: string;
    onlineDevicePercentage: string;
    coveragePercentage: string;
    compliantDevicePercentage: string;
    numberOfDevices: number;
    onlineDevices: number;
    devicesWithConnectionStatus: number;
    devicesWithPolicy: number;
    compliantDevices: number;
    policyId: string | null;
    policyName: string;
    hasEmptyPolicy: boolean;
    id: number;
    parentId: number | null;
    children: GroupOverviewData[];
}

function addChildrenProperty(data: GroupOverviewData[]) {
    const map = new Map<number, GroupOverviewData>();

    data.forEach((item) => {
        map.set(item.id, { ...item, children: [] });
    });

    data.forEach((item) => {
        if (item.parentId !== null) {
            const parent = map.get(item.parentId);
            const child = map.get(item.id);
            if (parent && child) {
                parent.children.push(child);
            }
        }
    });

    const groupIds = data.map((item) => item.id);
    return Array.from(map.values()).filter((item) => item.parentId === null || !groupIds.includes(item.parentId));
}

export const getGroupsTableData = (groups: Group[]): GroupOverviewData[] => {
    const groupArray: GroupOverviewData[] = [];

    const emptyStatistics = {
        deviceCount: 0,
        connectionStatus: { online: 0, offline: 0 },
        policyStatus: { compliant: 0, nonCompliant: 0, noPolicy: 0 },
    };

    groups.forEach((group) => {
        const stats = group.statistics?.aggregated ?? emptyStatistics;
        const numberOfDevices = stats.deviceCount;
        const devicesWithConnectionStatus = stats.connectionStatus.online + stats.connectionStatus.offline;
        const onlineDevicePercentage =
            devicesWithConnectionStatus > 0
                ? `${toWholePercent(stats.connectionStatus.online, devicesWithConnectionStatus)}%`
                : "";
        const devicesWithPolicy = numberOfDevices - stats.policyStatus.noPolicy;
        const coveragePercentage = devicesWithPolicy > 0 ? `${toWholePercent(devicesWithPolicy, numberOfDevices)}%` : "";
        const compliantDevices = stats.policyStatus.compliant;
        const compliantDevicePercentage = devicesWithPolicy > 0 ? `${toWholePercent(compliantDevices, devicesWithPolicy)}%` : "";

        const newGroupData = {
            name: group.name,
            description: group.description || "-",
            onlineDevicePercentage,
            coveragePercentage,
            compliantDevicePercentage,
            numberOfDevices,
            onlineDevices: stats.connectionStatus.online,
            devicesWithConnectionStatus,
            devicesWithPolicy,
            compliantDevices,
            policyId: group.policyId,
            policyName: group.policyName ?? "",
            hasEmptyPolicy: !!group.policyId && !hasEffectivePolicy(group),
            id: group.id,
            parentId: group.parentId,
            children: [],
        };

        groupArray.push(newGroupData);
    });

    return addChildrenProperty(groupArray);
};

export const getGroupIdsFromNameColumnFilter = (columnFilters: ColumnFiltersState): number[] => {
    const value = columnFilters.find((filter) => filter.id === "name")?.value;
    if (!Array.isArray(value) || value.length === 0) {
        return [];
    }
    return value.map((id) => Number(id));
};

const getClosestSelectedAncestorId = (
    groupId: number,
    selectedIds: Set<number>,
    groupsById: Map<number, Group>,
): number | null => {
    let candidateParentId = groupsById.get(groupId)?.parentId ?? null;
    while (candidateParentId !== null && !selectedIds.has(candidateParentId)) {
        candidateParentId = groupsById.get(candidateParentId)?.parentId ?? null;
    }
    return candidateParentId;
};

export const buildGroupTableDataForSelectionFilter = (
    fullTree: GroupOverviewData[],
    allGroups: Group[],
    selectedIds: number[],
): GroupOverviewData[] => {
    const selectedSet = new Set(selectedIds);
    const groupsById = new Map(allGroups.map((group) => [group.id, group]));
    const overviewById = new Map<number, GroupOverviewData>();
    const orderedSelectedIds: number[] = [];

    const addGroup = (group: GroupOverviewData) => {
        overviewById.set(group.id, group);
        if (selectedSet.has(group.id)) {
            orderedSelectedIds.push(group.id);
        }
        group.children.forEach(addGroup);
    };
    fullTree.forEach(addGroup);

    const rows: GroupOverviewData[] = [];
    for (const id of orderedSelectedIds) {
        const sourceRow = overviewById.get(id);
        if (!sourceRow) {
            continue;
        }
        rows.push({
            ...sourceRow,
            parentId: getClosestSelectedAncestorId(id, selectedSet, groupsById),
            children: [],
        });
    }

    return addChildrenProperty(rows);
};

export const getExpandedStateForGroup = (
    targetGroupId: number,
    groups: Group[],
    baseExpanded: ExpandedState,
): Record<string, boolean> => {
    const parentGroups = getParentGroups(targetGroupId, groups);

    if (parentGroups.length === 0) {
        return baseExpanded === true ? {} : baseExpanded;
    }

    const expandedState = baseExpanded === true ? {} : { ...baseExpanded };

    parentGroups.forEach((group) => {
        expandedState[String(group.id)] = true;
    });

    return expandedState;
};

// Pagination slices top-level rows only, so a nested group shares a page with its root ancestor.
export const findRootRowIndexForGroup = (rows: Row<GroupOverviewData>[], groupId: number): number => {
    const containsGroup = (row: Row<GroupOverviewData>): boolean =>
        row.original.id === groupId || row.subRows.some(containsGroup);

    return rows.findIndex(containsGroup);
};

// A search hit on a value the table never shows would list the row without anything highlighted in it.
const unsearchableFields = new Set<keyof GroupOverviewData>([
    "policyId",
    "onlineDevices",
    "devicesWithConnectionStatus",
    "devicesWithPolicy",
    "compliantDevices",
]);

export const customGlobalFilterFn: FilterFnOption<GroupOverviewData> = (row, _columnId, filterValue) => {
    if (!filterValue) {
        return true;
    }
    const normalizedFilterValue = String(filterValue).toLowerCase();

    const checkRowMatch = (rowData: GroupOverviewData): boolean => {
        const rowMatches = Object.entries(rowData).some(([field, value]) => {
            if (unsearchableFields.has(field as keyof GroupOverviewData)) {
                return false;
            }
            return (
                (typeof value === "string" || typeof value === "number") &&
                String(value).toLowerCase().includes(normalizedFilterValue)
            );
        });

        if (rowData.children && rowData.children.length > 0) {
            return rowMatches || rowData.children.some(checkRowMatch);
        }

        return rowMatches;
    };

    return checkRowMatch(row.original);
};

export const getColumns = (globalFilter?: string): ColumnDef<GroupOverviewData, unknown>[] => [
    {
        header: (info) => <ExpandedHeader title={t("pages:groupPage.table.header.name")} {...info} />,
        accessorKey: "name",
        enableSorting: true,
        cell: (info) => (
            <ExpandedCell {...info} shouldOpenSidePanel>
                <NameCell id={info.row.original.id} value={info.getValue<string>()} globalFilter={globalFilter} />
            </ExpandedCell>
        ),
        filterFn: (row, _columnId, filterValue: string[]) => {
            if (filterValue.length === 0) {
                return true;
            }
            return filterValue.includes(String(row.original.id));
        },
    },
    {
        header: t("common:label.description"),
        accessorKey: "description",
        cell: (info) => <DescriptionCell value={info.getValue<string>()} globalFilter={globalFilter} />,
        enableSorting: true,
        meta: { fluid: true, fluidMinWidth: "24ch" },
    },
    {
        header: t("pages:groupPage.table.header.devices"),
        meta: { headerTooltip: t("pages:groupPage.table.headerTooltip.devices") },
        accessorKey: "numberOfDevices",
        cell: (info) => <DevicesCountCell {...info} />,
        enableSorting: true,
        sortingFn: "basic",
    },
    {
        header: t("common:status.online"),
        meta: { headerTooltip: t("pages:groupPage.table.headerTooltip.online") },
        accessorKey: "onlineDevicePercentage",
        cell: (info) => (
            <TableCell {...info}>
                <OnlineStatusIcon {...info} />
            </TableCell>
        ),
        enableSorting: true,
        sortingFn: "alphanumeric",
    },
    {
        header: t("pages:groupPage.table.header.coverage"),
        meta: { headerTooltip: t("pages:groupPage.table.headerTooltip.coverage") },
        accessorKey: "coveragePercentage",
        cell: (info) => (
            <TableCell {...info}>
                <CoverageStatusIcon {...info} />
            </TableCell>
        ),
        enableSorting: true,
        sortingFn: "alphanumeric",
    },
    {
        header: t("common:label.compliance"),
        meta: { headerTooltip: t("pages:groupPage.table.headerTooltip.compliance") },
        accessorKey: "compliantDevicePercentage",
        cell: (info) => (
            <TableCell {...info}>
                <ComplianceStatusIcon {...info} />
            </TableCell>
        ),
        enableSorting: true,
    },
    {
        header: t("common:label.policy"),
        accessorKey: "policyName",
        cell: (info) => <PolicyCell {...info} />,
        enableSorting: true,
    },
    {
        header: "",
        accessorKey: "action",
        cell: (info) => <ActionCell {...info} />,
        enableSorting: true,
    },
];
